import { Box, Heading, IconButton, Separator, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { FaLink } from 'react-icons/fa';
import type { LearnMoreSection } from '../LearnMore';
import useLearnMoreSections from '../hooks/useLearnMoreSections';

const SECTIONS: LearnMoreSection[] = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'sign-up-and-renewal', label: 'Sign up & renewal' },
  { id: 'prospective-members', label: 'Prospective members' },
  { id: 'non-paid-up-members', label: 'Non paid up members' },
  { id: 'family-members', label: 'Family members' },
];

const MembershipManagement = (): React.JSX.Element => {
  useLearnMoreSections(SECTIONS, { pageTitle: 'Membership Management' });

  return (
    <VStack align="stretch" gap={12}>
      <Box>
        <Heading size="3xl" letterSpacing="-0.04em">
          Membership management
        </Heading>
        <Text marginTop={4} color="gray.600" fontSize="lg" maxW="3xl" _dark={{ color: 'gray.300' }}>
          Olympiq keeps website, mobile app, and payments in sync so you always know who is paid up,
          who is trialling, and who needs attention. Everything lives in one system - and is nicely
          visualised in your club website admin area.
        </Text>
      </Box>

      <Separator />

      <SectionBlock id="introduction" title="Introduction">
        <Text>
          Every member interaction starts in one place: your club website. Once someone joins,
          Olympiq immediately provisions access to your club&apos;s mobile app account. Coaches see
          accurate attendance lists, the treasurer sees up-to-date payment records, and content
          stays consistent everywhere.
        </Text>
        <Text>
          You can even allow partner clubs to join the club ecosystem with limited, event-specific
          access. They see only the sessions you specify.
        </Text>
      </SectionBlock>

      <SectionBlock id="sign-up-and-renewal" title="Sign up & renewal">
        <Text>
          Members sign up and renew directly on your website or inside the mobile app - one login
          powers both surfaces.
        </Text>
        <Text>
          Automated renewal emails and in-app reminders nudge members before their expiry date,
          removing the need for admin follow-ups.
        </Text>
        <Text>
          When someone doesn’t renew, their access is removed automatically across the website and
          app, keeping data clean and permissions accurate.
        </Text>
        <Text>
          Reward key volunteers by granting complimentary annual memberships that keep them active
          without manual workarounds.
        </Text>
        <Text>
          Have the ability to work with partner sport clubs to enhance the club sessions available
          to both clubs&apos; members.
        </Text>
      </SectionBlock>

      <SectionBlock id="prospective-members" title="Prospective members">
        <Text>
          Prospective members receive a two-week access window in the mobile app to explore
          sessions, posts, and announcements.
        </Text>
        <Text>
          During the trial they can book taster sessions, see upcoming events, and ask questions
          through posts or comments.
        </Text>
        <Text>
          If they haven’t paid by the end of the window, their account is automatically removed—no
          spreadsheets or follow-up lists.
        </Text>
      </SectionBlock>

      <SectionBlock id="non-paid-up-members" title="Non paid up members">
        <Text>
          Non paid up members lose booking privileges immediately, and after a 1 month grace period
          their account is automatically deleted along with their data.
        </Text>
        <Text>
          They automatically recieve a goodbye email so they know how to rejoin when they&apos;re
          ready.
        </Text>
      </SectionBlock>

      <SectionBlock id="family-members" title="Family members">
        <Text>
          Account linking lets parents or guardians manage multiple junior profiles under a single
          login so everyday admin happens in one place.
        </Text>
        <Text>
          Linked adults can book sessions, renew memberships, and manage emergency contacts for each
          child without juggling separate credentials.
        </Text>
        <Text>
          Mobile notifications and emails go to both the parent and the junior member (where
          appropriate) so everyone stays informed.
        </Text>
        <Text>
          Linked accounts also support grandparents, carers, or team managers, so anyone helping a
          junior or dependent member can keep their information current without extra admin work.
        </Text>
      </SectionBlock>
    </VStack>
  );
};

interface SectionBlockProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

const SectionBlock = ({ id, title, children }: SectionBlockProps): React.JSX.Element => (
  <Box as="section" id={id} scrollMarginTop="120px">
    <Heading size="2xl" fontWeight="bold" marginBottom={3}>
      {title}
      <IconButton
        aria-label="Copy link"
        variant="ghost"
        size="xs"
        onClick={() => {
          navigator.clipboard.writeText(window.location.href);
        }}
      >
        <FaLink />
      </IconButton>
    </Heading>
    <VStack align="stretch" gap={3}>
      {typeof children === 'string' ? <Text color="gray.600">{children}</Text> : children}
    </VStack>
  </Box>
);

export default MembershipManagement;
